/* eslint-disable arrow-body-style */
import { type ComponentPropsWithoutRef, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from './ErrorMessage';
import {
  containsReservedCharacters,
  getInputRules,
  stripReservedCharacters,
  validateField,
} from '../../utils/form-utils';
import { textinputMaxLength } from '../../constants';
import Input from './TextInput';
import type { ErrorMessages, Rules } from '../../config/types';
import Details from './Details';

type Props = {
  name1: string;
  name2: string;
  title1: string;
  title2: string;
  width?: 'full' | 'three-quarters' | 'two-thirds' | 'one-half' | 'one-third' | 'one-quarter';
  rules1?: Rules;
  rules2?: Rules;
  errorMessages1?: ErrorMessages;
  errorMessages2?: ErrorMessages;
  detailsHeading?: string;
  detailsContent?: string;
} & ComponentPropsWithoutRef<'input'>;

const TwoTextBoxInput = (props: Props) => {
  const {
    name1,
    name2,
    title1,
    title2,
    rules1,
    rules2,
    disabled,
    defaultValue,
    errorMessages1,
    errorMessages2,
    detailsHeading,
    detailsContent,
  } = props;

  const { register, getValues, getFieldState, setValue } = useFormContext();

  const type1 = rules1?.number ? 'number' : rules1?.email ? 'email' : 'text';
  const type2 = rules2?.number ? 'number' : rules2?.email ? 'email' : 'text';

  const inputRules1 = useMemo(
    () => ({ ...getInputRules(rules1 ?? {}, title1, textinputMaxLength) }),
    [rules1, title1],
  );
  const inputRules2 = useMemo(
    () => ({ ...getInputRules(rules2 ?? {}, title2, textinputMaxLength) }),
    [rules2, title2],
  );

  useEffect(() => {
    if (getValues(name1) === undefined) {
      setValue(name1, defaultValue ?? '');
    }
    if (getValues(name2) === undefined) {
      setValue(name2, defaultValue ?? '');
    }
  }, []);

  const hooksFormRegister1 = register(name1, {
    validate: (value: string) => validateField(title1, inputRules1, value, errorMessages1),
  });

  const hooksFormRegister2 = register(name2, {
    validate: (value: string) => validateField(title2, inputRules2, value, errorMessages2),
  });

  const { error: error1 } = getFieldState(name1);
  const { error: error2 } = getFieldState(name2);

  return (
    <>
      {error1 && <ErrorMessage error={error1} />}
      {error2 && <ErrorMessage error={error2} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <Input
        // {...props}
        {...hooksFormRegister1}
        name={name1}
        disabled={disabled ?? false}
        onKeyDown={(e) => {
          if (
            e.key.length === 1 &&
            containsReservedCharacters(e.key, inputRules1.reservedCharacters)
          )
            e.preventDefault();
        }}
        onPaste={(e) => {
          const clipboardData = e.clipboardData.getData('text');
          if (containsReservedCharacters(clipboardData, inputRules1.reservedCharacters)) {
            e.preventDefault();
            const sanitized = stripReservedCharacters(
              clipboardData,
              inputRules1.reservedCharacters,
            );
            const target = e.target as HTMLInputElement;
            const start = target.selectionStart ?? target.value.length;
            const end = target.selectionEnd ?? target.value.length;
            const nextValue = `${target.value.slice(0, start)}${sanitized}${target.value.slice(
              end,
            )}`;
            setValue(name1, nextValue, { shouldDirty: true, shouldValidate: true });
            requestAnimationFrame(() => {
              const cursor = start + sanitized.length;
              target.selectionStart = cursor;
              target.selectionEnd = cursor;
            });
          }
        }}
        numeric={!!rules1?.number}
        type={type1}
      />

      <Input
        // {...props}
        {...hooksFormRegister2}
        name={name2}
        disabled={disabled ?? false}
        onKeyDown={(e) => {
          if (
            e.key.length === 1 &&
            containsReservedCharacters(e.key, inputRules2.reservedCharacters)
          )
            e.preventDefault();
        }}
        onPaste={(e) => {
          const clipboardData = e.clipboardData.getData('text');
          if (containsReservedCharacters(clipboardData, inputRules2.reservedCharacters)) {
            e.preventDefault();
            const sanitized = stripReservedCharacters(
              clipboardData,
              inputRules2.reservedCharacters,
            );
            const target = e.target as HTMLInputElement;
            const start = target.selectionStart ?? target.value.length;
            const end = target.selectionEnd ?? target.value.length;
            const nextValue = `${target.value.slice(0, start)}${sanitized}${target.value.slice(
              end,
            )}`;
            setValue(name2, nextValue, { shouldDirty: true, shouldValidate: true });
            requestAnimationFrame(() => {
              const cursor = start + sanitized.length;
              target.selectionStart = cursor;
              target.selectionEnd = cursor;
            });
          }
        }}
        numeric={!!rules2?.number}
        type={type2}
      />
    </>
  );
};

TwoTextBoxInput.displayName = 'TwoTextBoxInput';

export default TwoTextBoxInput;
